/**
 * Ottavine review photo viewer. Wraps the reviews section (sections/ott-reviews.liquid): any button
 * inside it with `data-ott-full-src` opens that photo full screen in the section's
 * `<dialog class="ott-lightbox">`. The arrow buttons, the arrow keys and a swipe step through the
 * section's distinct photos; Escape, the close button or a tap beside the photo closes it, and focus
 * goes back to the photo that opened it.
 */
class OttReviewLightbox extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {HTMLImageElement | null} */
  #image = null;
  /** @type {HTMLElement | null} */
  #count = null;
  /** @type {{ src: string, alt: string }[]} */
  #photos = [];
  #index = 0;
  /** @type {HTMLElement | null} */
  #opener = null;
  /** @type {number | null} */
  #touchX = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog.ott-lightbox');
    if (!this.#dialog) return;

    this.#count = this.#dialog.querySelector('.ott-lightbox__count');
    this.#image = document.createElement('img');
    this.#image.className = 'ott-lightbox__image';
    this.#image.decoding = 'async';
    this.#dialog.insertBefore(this.#image, this.#count);

    this.addEventListener('click', this.#onClick);
    this.#dialog.addEventListener('keydown', this.#onKeydown);
    this.#dialog.addEventListener('close', this.#onClose);
    this.#dialog.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    this.#dialog.addEventListener('touchend', this.#onTouchEnd);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#dialog?.removeEventListener('keydown', this.#onKeydown);
    this.#dialog?.removeEventListener('close', this.#onClose);
    this.#dialog?.removeEventListener('touchstart', this.#onTouchStart);
    this.#dialog?.removeEventListener('touchend', this.#onTouchEnd);
  }

  /** The section's photos, each once: the row and the review cards show the same pictures. */
  #collectPhotos() {
    /** @type {Map<string, string>} */
    const photos = new Map();
    for (const trigger of this.querySelectorAll('[data-ott-full-src]')) {
      if (!(trigger instanceof HTMLElement)) continue;
      const src = trigger.dataset.ottFullSrc;
      if (src && !photos.has(src)) photos.set(src, trigger.dataset.ottAlt || '');
    }
    this.#photos = [...photos].map(([src, alt]) => ({ src, alt }));
  }

  /** @param {number} index */
  #show(index) {
    const total = this.#photos.length;
    if (!total || !this.#image) return;

    this.#index = (index + total) % total;
    const photo = this.#photos[this.#index];
    if (!photo) return;
    this.#image.src = photo.src;
    this.#image.alt = photo.alt;
    if (this.#count) this.#count.textContent = `${this.#index + 1} / ${total}`;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const dialog = this.#dialog;
    const target = event.target;
    if (!dialog || !(target instanceof Element)) return;

    if (target.closest('[data-ott-lightbox-close]') || target === dialog) {
      dialog.close();
      return;
    }

    const step = target.closest('[data-ott-lightbox-step]');
    if (step instanceof HTMLElement) {
      this.#show(this.#index + Number(step.dataset.ottLightboxStep));
      return;
    }

    const trigger = target.closest('[data-ott-full-src]');
    if (!(trigger instanceof HTMLElement) || dialog.open) return;

    this.#collectPhotos();
    this.#opener = trigger;
    dialog.toggleAttribute('data-single', this.#photos.length < 2);
    this.#show(Math.max(0, this.#photos.findIndex((photo) => photo.src === trigger.dataset.ottFullSrc)));
    dialog.showModal();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'ArrowRight') this.#show(this.#index + 1);
    if (event.key === 'ArrowLeft') this.#show(this.#index - 1);
  };

  #onClose = () => {
    this.#opener?.focus();
    this.#opener = null;
  };

  /** @param {TouchEvent} event */
  #onTouchStart = (event) => {
    this.#touchX = event.changedTouches[0]?.clientX ?? null;
  };

  /** @param {TouchEvent} event */
  #onTouchEnd = (event) => {
    if (this.#touchX === null) return;
    const distance = (event.changedTouches[0]?.clientX ?? this.#touchX) - this.#touchX;
    this.#touchX = null;
    if (Math.abs(distance) > 40) this.#show(this.#index + (distance < 0 ? 1 : -1));
  };
}

if (!customElements.get('ott-review-lightbox')) {
  customElements.define('ott-review-lightbox', OttReviewLightbox);
}
